import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

export type NotificationType = 'success' | 'error' | 'warning' | 'info' | 'confirm';

export interface NotificationConfig {
  type: NotificationType;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  actionText?: string;  // For action-based notifications
  onConfirm?: () => void;
  onCancel?: () => void;
  onAction?: () => void;  // Callback for action button
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationSubject = new Subject<NotificationConfig>();
  public notification$ = this.notificationSubject.asObservable();

  /**
   * Show a success notification
   */
  success(title: string, message: string = ''): void {
    this.notificationSubject.next({
      type: 'success',
      title,
      message
    });
  }

  /**
   * Show an error notification
   */
  error(title: string, message: string = ''): void {
    this.notificationSubject.next({
      type: 'error',
      title,
      message
    });
  }

  /**
   * Show a warning notification
   */
  warning(title: string, message: string = ''): void {
    this.notificationSubject.next({
      type: 'warning',
      title,
      message
    });
  }

  /**
   * Show an info notification
   */
  info(title: string, message: string = ''): void {
    this.notificationSubject.next({
      type: 'info',
      title,
      message
    });
  }

  /**
   * Show a confirmation dialog
   */
  confirm(
    title: string,
    message: string,
    onConfirm: () => void,
    onCancel?: () => void,
    confirmText: string = 'Confirmar',
    cancelText: string = 'Cancelar'
  ): void {
    this.notificationSubject.next({
      type: 'confirm',
      title,
      message,
      confirmText,
      cancelText,
      onConfirm,
      onCancel
    });
  }

  /**
   * Show a notification with an action button (e.g., for undo functionality)
   */
  showWithAction(
    message: string,
    actionText: string,
    onAction: () => void
  ): void {
    this.notificationSubject.next({
      type: 'info',
      title: '',
      message,
      actionText,
      onAction
    });
  }
}
